import {
  AUTHENTICATION_FLOW_STATE,
  AuthenticationFlowState
} from '~/src/stores/authentication-flow'
import { useEoState } from '~/src/hooks/useEoState'
import { useLoginAsCustomer } from '~/src/hooks/auth/useLoginAsCustomer'
import { useEffect } from 'react'
import { UseAuthenticationFlowDialogsReturnType } from '~/src/hooks/auth/flow/useAuthenticationFlowDialogs'
import { Customer, ValidateAskResetPasswordResponse } from '@eo-storefronts/eo-core'
import { AuthenticationFlowDialogNameEnum } from '~/src/types/DialogNameEnum'
import { useActivateAccount } from '~/src/hooks/customer/useActivateAccount'
import useValidateAskResetPassword from '~/src/hooks/customer/useValidateAskResetPassword'

interface ReturnsType {
  onSubmit(): Promise<void>,
}

interface Props extends Pick<UseAuthenticationFlowDialogsReturnType, 'end' | 'goToStep'> {
}

const useAuthenticationFlowConfirmationCodeOnSubmit = ({ end, goToStep }: Props): ReturnsType => {
  const { activateAccount, errors: activateAccountErrors, loading: activateAccountLoading } = useActivateAccount()
  const { login, errors: loginErrors, loading: loginLoading } = useLoginAsCustomer()
  const { validateAskResetPassword, errors: validateErrors, loading: validateLoading } = useValidateAskResetPassword()
  const [ authenticationFlowState, setAuthenticationFlowState ] = useEoState<AuthenticationFlowState>(AUTHENTICATION_FLOW_STATE)

  const _handleLoginWithCode = async (): Promise<void> => {
    if (
      (!authenticationFlowState.customer?.email && !authenticationFlowState.customer?.phone) ||
      !authenticationFlowState.confirmationCode
    ) {
      return
    }

    const response: Customer | null = await login({
      username: (authenticationFlowState.customer.email || authenticationFlowState.customer.phone)!,
      code: authenticationFlowState.confirmationCode
    })

    if (!response) {
      return
    }

    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      customer: {
        id: response?.id,
        uid: response?.uid,
        email: response?.email,
        phone: response?.phone,
        company: response?.company,
        firstName: response?.firstName,
        lastName: response?.lastName,
        password: response?.password
      }
    }))

    end()
  }

  const _handleAccountActivationWithCode = async (): Promise<void> => {
    if (!authenticationFlowState.confirmationCode || !authenticationFlowState.customer?.phone) {
      return
    }

    const response = await activateAccount({
      phone: authenticationFlowState.customer.phone,
      code: authenticationFlowState.confirmationCode
    })

    if (!response) {
      return
    }

    goToStep(AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_SUCCESSFUL)
  }

  const _handleAskResetPasswordWithCode = async (): Promise<void> => {
    if (!authenticationFlowState.confirmationCode || !authenticationFlowState.customer?.phone) {
      return
    }

    const response: ValidateAskResetPasswordResponse | undefined = await validateAskResetPassword({
      phone: authenticationFlowState.customer.phone,
      code: authenticationFlowState.confirmationCode
    })

    if (!response || !response.token || !response.validity) {
      return
    }

    const url: URL = new URL(window.location.href)

    url.searchParams.set('token', response.token)
    url.searchParams.set('timestamp', response.validity)
    window.history.pushState({}, '', url.toString())

    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      type: 'PASSWORD_RESET',
      validateAskResetPasswordResponse: response
    }))
    goToStep(AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_RESET_PASSWORD)
  }

  const onSubmit = async (): Promise<void> => {
    if (authenticationFlowState.type === 'ACTIVATION') {
      return _handleAccountActivationWithCode()
    } else if (authenticationFlowState.type === 'PASSWORD_RESET') {
      return _handleAskResetPasswordWithCode()
    }

    return _handleLoginWithCode()
  }

  useEffect(() => {
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      isLoading: loginLoading || activateAccountLoading || validateLoading
    }))
  }, [ loginLoading, activateAccountLoading, validateLoading ])


  useEffect(() => {
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      errors: loginErrors || activateAccountErrors || validateErrors
    }))
  }, [ loginErrors, activateAccountErrors, validateErrors ])

  return {
    onSubmit
  }
}

export default useAuthenticationFlowConfirmationCodeOnSubmit
